import * as rxjs from "rxjs";
import * as rxjsOperators from "rxjs/operators";
import * as util from "../../util";
import * as JsonResponse1 from "../../../../domain/models/JsonResponse1";
import * as JsonResponseMeta1 from "../../../../domain/models/JsonResponseMeta1";
import * as FirstPartyFetchResponse from "../../../../domain/models/FirstPartyFetchResponse";
import * as TForm from "../../models/TForm";
import * as request from "../request";
import { TState, TSubmitMethod, TModelFile } from "./lensBaseTypes";
import { reduceDataToStateUpdate } from "./reduceDataToStateUpdate";
import { set } from "./set";
import { TGetState } from "../../state/TGetState";
import { TSetState } from "../../state/TSetState";
import { TActionPayload } from "../../state/actions/TAction";

interface ILensSubmitForFormList<A extends string, P, V extends JsonResponse1.T<V["data"], JsonResponseMeta1.T>, S> {
    // 5 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3],
        K5 extends keyof S[K1][K2][K3][K4]
    >
    (
        path: [K1, K2, K3, K4, K5],
        urlCallback: (
            data: P,
            getState: TGetState
        ) => string,
        payloadCallback: (
            data: P,
            getState: TGetState
        ) => unknown,
        method: TSubmitMethod,
        responseModel: TModelFile<V>,
    ): (
        obs$: rxjs.Observable<TActionPayload<A, P>>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
    // 4 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2],
        K4 extends keyof S[K1][K2][K3]
    >
    (
        path: [K1, K2, K3, K4],
        urlCallback: (
            data: P,
            getState: TGetState
        ) => string,
        payloadCallback: (
            data: P,
            getState: TGetState
        ) => unknown,
        method: TSubmitMethod,
        responseModel: TModelFile<V>,
    ): (
        obs$: rxjs.Observable<TActionPayload<A, P>>,
        getState: TGetState,
        setState: TSetState,
    ) => rxjs.Observable<unknown>;
    // 3 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1],
        K3 extends keyof S[K1][K2]
    >
    (
        path: [K1, K2, K3],
        urlCallback: (
            data: P,
            getState: TGetState
        ) => string,
        payloadCallback: (
            data: P,
            getState: TGetState
        ) => unknown,
        method: TSubmitMethod,
        responseModel: TModelFile<V>,
    ): (
        obs$: rxjs.Observable<TActionPayload<A, P>>,
        getState: TGetState,
        setState: TSetState,
    ) => rxjs.Observable<unknown>;
    // 2 level lens path guard
    <
        K1 extends keyof S,
        K2 extends keyof S[K1]
    >
    (
        path: [K1, K2],
        urlCallback: (
            data: P,
            getState: TGetState
        ) => string,
        payloadCallback: (
            data: P,
            getState: TGetState
        ) => unknown,
        method: TSubmitMethod,
        responseModel: TModelFile<V>,
    ): (
        obs$: rxjs.Observable<TActionPayload<A, P>>,
        getState: TGetState,
        setState: TSetState,
    ) => rxjs.Observable<unknown>;
    // 1 level lens path guard
    <K1 extends keyof S>
    (
        path: [K1],
        urlCallback: (
            data: P,
            getState: TGetState
        ) => string,
        payloadCallback: (
            data: P,
            getState: TGetState
        ) => unknown,
        method: TSubmitMethod,
        responseModel: TModelFile<V>,
    ): (
        obs$: rxjs.Observable<TActionPayload<A, P>>,
        getState: TGetState,
        setState: TSetState
    ) => rxjs.Observable<unknown>;
}

export const submitForFormList =
    <A extends string, P, V extends JsonResponse1.T<V["data"], JsonResponseMeta1.T>>(): ILensSubmitForFormList<A, P, V, TState> =>
        (
            // There is no way to create paramater overloads here so we set to any
            lensPath: any, // eslint-disable-line
            urlCallback: any, // eslint-disable-line
            payloadCallback: any, // eslint-disable-line
            requestMethod: TSubmitMethod,
            responseModel: TModelFile<V>,
        ) =>
            (
                obs$: rxjs.Observable<TActionPayload<A, P>>,
                getState: TGetState,
                setState: TSetState
            ) =>
                obs$.pipe(
                    rxjsOperators.mergeMap((action) =>
                        request.payloadFetchArray<V>(
                            urlCallback(action.payload, getState),
                            requestMethod,
                            payloadCallback(action.payload, getState),
                            responseModel.newDefault(),
                            responseModel.codec,
                        )()
                    ),
                    rxjsOperators.tap<FirstPartyFetchResponse.T<V>>((response) => util.defaultCRMRequestErrorHandler(response)),
                    rxjsOperators.tap<FirstPartyFetchResponse.T<V>>(
                        reduceDataToStateUpdate<FirstPartyFetchResponse.T<V>>(setState)(
                            set<FirstPartyFetchResponse.T<V>>()(
                                lensPath,
                                /* eslint-disable */
                                // The type is always expected to be never for the form, since we have abstraced the lens scoping,
                                // which is untrue
                                // @ts-ignore
                                TForm.requestToFormList
                                /* eslint-enable */
                            )
                        )
                    ),
                );
